import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com";
const REDIRECT_URL = window.location.origin + "/";

function formatApiError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

const LoginPage = () => {
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGoogleLogin = () => {
    window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(REDIRECT_URL)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const endpoint = mode === "register" ? "/api/auth/register" : "/api/auth/login";
      const payload = mode === "register"
        ? { email, password, name }
        : { email, password };

      const { data } = await axios.post(`${API}${endpoint}`, payload, {
        withCredentials: true,
      });

      toast.success(mode === "register" ? "Account created!" : "Welcome back!");
      window.location.href = "/";
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel — Dark Navy Brand */}
      <div className="hidden lg:flex lg:w-[48%] bg-[#0f172a] flex-col items-center justify-center relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #c9952b 1px, transparent 0)`,
          backgroundSize: "40px 40px"
        }} />

        <div className="relative z-10 text-center px-12 max-w-lg">
          <img
            src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif"
            alt="Owl Intelligence"
            className="w-24 h-24 mx-auto mb-8"
          />
          <h1 className="text-3xl font-light text-white tracking-[4px] font-serif mb-3">
            OWL INTELLIGENCE
          </h1>
          <div className="w-12 h-[2px] bg-[#c9952b] mx-auto mb-6" />
          <p className="text-slate-300 text-base leading-relaxed mb-10">
            AI-powered newsletters with deep web research,<br />
            tailored to your role and industry.
          </p>

          {/* Feature list */}
          <div className="space-y-5 text-left">
            {[
              { num: "01", text: "Deep research from live web sources" },
              { num: "02", text: "6-8 tailored sections per newsletter" },
              { num: "03", text: "Schedule & share with your team" },
            ].map((item) => (
              <div key={item.num} className="flex items-center gap-4">
                <span className="text-[#c9952b] font-mono text-sm font-bold w-6">{item.num}</span>
                <div className="h-px bg-[#c9952b]/40 w-6" />
                <span className="text-white text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom info */}
        <div className="absolute bottom-8 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-[#c9952b]/30 bg-[#c9952b]/10">
            <span className="text-[#c9952b] text-sm font-semibold">$4.99/mo</span>
            <span className="text-[#c9952b]/50">&middot;</span>
            <span className="text-white text-sm font-medium">1-week free trial</span>
          </div>
        </div>
      </div>

      {/* Right Panel — Auth Form */}
      <div className="flex-1 flex items-center justify-center bg-[#faf9f6] px-6 sm:px-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img
              src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif"
              alt="Owl"
              className="w-10 h-10"
            />
            <span className="text-lg font-serif tracking-[2px] text-[#0f172a]">OWL INTELLIGENCE</span>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#0f172a] tracking-tight">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {mode === "login"
                ? "Sign in to access your newsletters"
                : "Start your 1-week free trial"}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div data-testid="auth-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Email/Password Form */}
          <form onSubmit={handleEmailAuth} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Full Name</Label>
                <Input
                  data-testid="name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="mt-1.5 h-11 bg-white border-slate-200 focus:border-[#c9952b] focus:ring-[#c9952b]/20"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Email</Label>
              <Input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="mt-1.5 h-11 bg-white border-slate-200 focus:border-[#c9952b] focus:ring-[#c9952b]/20"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "register" ? "Min. 6 characters" : "Enter password"}
                  required
                  minLength={mode === "register" ? 6 : 1}
                  className="h-11 bg-white border-slate-200 focus:border-[#c9952b] focus:ring-[#c9952b]/20 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              data-testid="email-auth-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium tracking-wide transition-all active:scale-[0.98]"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {mode === "login" ? "Sign In" : "Create Account"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <Separator className="flex-1" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
            <Separator className="flex-1" />
          </div>

          {/* Google OAuth */}
          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-11 border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-medium transition-all active:scale-[0.98]"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>

          {/* Toggle mode */}
          <p className="text-center text-sm text-slate-500 mt-6">
            {mode === "login" ? (
              <>
                Don't have an account?{" "}
                <button
                  data-testid="switch-to-register"
                  onClick={() => { setMode("register"); setError(""); }}
                  className="text-[#c9952b] font-semibold hover:underline"
                >
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  data-testid="switch-to-login"
                  onClick={() => { setMode("login"); setError(""); }}
                  className="text-[#c9952b] font-semibold hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </p>

          <p className="text-center text-[11px] text-slate-400 mt-8">
            By continuing, you agree to our terms and privacy policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
